import React from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react';
import FaceStore from '../stores/FaceStore';
import { COLORS, BREAKPOINT_SIZE, MARGIN, SectionHeading, SectionParagraph } from '../theme';
import Arrows from './Arrows';
import FACE_MATCH_THRESHOLDS from '../data/faceMatchThresholds';
const PUBLIC_PATH = process.env.PUBLIC_URL;
const VERIFICATION_FACE_MARGIN = '30px';
const VERIFICATION_FACE_WIDTH = '65px';
const VERIFICATION_FACE_HEIGHT = '88px';
const IDENTIFICATION_MARGIN = '15px';
const getImgUrl = (isGrey: boolean, imgUrl?: string) => {
  return `${PUBLIC_PATH}/img/${isGrey ? 'grey/' : 'color/'}${imgUrl}.png`;
};

// Responsive sizes based on the design
const GRID_THUMBNAILS_CONTAINER_DESKTOP_SIZE = 428;
const GRID_THUMBNAILS_CONTAINER_MOBILE_SIZE = 236;

const Section = styled.div<{ hasBorderLeft?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${p =>
    p.hasBorderLeft
      ? css`
        border-left: 1px solid  ${COLORS.mediumGrey};
        padding-left: 20px;
        @media (max-width: ${BREAKPOINT_SIZE.md}px) {
          border-left: none;
          padding-left: 0;
          padding-top: 16px;
          border-top: 1px solid  ${COLORS.mediumGrey};
        }
      }
    `
      : css`
        padding-right: 20px;
        @media (max-width: ${BREAKPOINT_SIZE.md}px) {
          padding-right: 0;
          padding-bottom: 16px;
        }
      }
    `}
`;

const SectionText = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0 ${MARGIN.large} 0;
`;

const NameSpan = styled.span`
  margin-bottom: ${VERIFICATION_FACE_MARGIN};
  @media (max-width: ${BREAKPOINT_SIZE.md}px) {
    margin-bottom: 0;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  margin-bottom: ${MARGIN.large};

  @media (max-width: ${BREAKPOINT_SIZE.md}px) {
    flex-direction: column;
    height: auto;
  }
`;

const FacesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${BREAKPOINT_SIZE.md}px) {
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
  }
`;

const SingleFaceContainer = styled.div`
  display: flex;
  justify-content: center;
  width: calc(100% * (44 / ${GRID_THUMBNAILS_CONTAINER_DESKTOP_SIZE}));
  margin-bottom: 10px;
  @media (max-width: ${BREAKPOINT_SIZE.sm}px) {
    width: calc(100% * (44 / ${GRID_THUMBNAILS_CONTAINER_MOBILE_SIZE}));
  }
`;

const FaceGrid = styled.div`
  width: 100%;
  margin-top: ${IDENTIFICATION_MARGIN};
  @media (max-width: ${BREAKPOINT_SIZE.md}px) {
    margin-top: 0;
  }
`;

const FaceItem = styled.div`
  float: left;
`;

const FaceIconContainer = styled.img<FaceIconProps>`
  width: 100%;
  /* NOTE: 100% instead auto to fix an issue 
  with the image stretching in Safari */
  height: 100%;
  /* to override WordPress styling */
  margin: 0 !important;

  ${p =>
    p.largeScreenMargin &&
    css`
      margin: ${VERIFICATION_FACE_MARGIN};
      @media (max-width: ${BREAKPOINT_SIZE.md}px) {
        margin: 0;
      }
    `}
`;

const FaceGridDesktop = styled.div`
  display: inline-block;
  @media (max-width: ${BREAKPOINT_SIZE.sm}px) {
    display: none;
  }

  ${FaceItem} {
    width: calc(100% * (44 / ${GRID_THUMBNAILS_CONTAINER_DESKTOP_SIZE}));
    box-sizing: border-box;
    margin-right: calc(100% * (4 / ${GRID_THUMBNAILS_CONTAINER_DESKTOP_SIZE}));
    &:nth-child(9n) {
      margin-right: 0;
    }
  }
`;

const FaceGridMobile = styled.div`
  display: none;
  @media (max-width: ${BREAKPOINT_SIZE.sm}px) {
    display: inline-block;
  }

  ${FaceItem} {
    width: calc(100% * (44 / ${GRID_THUMBNAILS_CONTAINER_MOBILE_SIZE}));
    height: auto;
    margin-right: calc(100% * (4 / ${GRID_THUMBNAILS_CONTAINER_MOBILE_SIZE}));
    &:nth-child(5n) {
      margin-right: 0;
    }
  }
`;

interface FaceProps {
  matchThreshold: number;
  isMatched?: boolean;
  imgUrl?: string;
}

interface FaceIconProps {
  isMatched?: boolean;
  imgUrl?: string;
  mobileLarge?: boolean;
  largeScreenMargin?: boolean;
}
// a higher threshold in the face store means precision, so less and less faces
// should be marked as matches as it gets higher
const Face = observer(({ imgUrl, matchThreshold }: FaceProps) => {
  const isMatched = matchThreshold >= FaceStore.matchThreshold;
  const url = getImgUrl(!isMatched, imgUrl);
  return <FaceIconContainer src={url}></FaceIconContainer>;
});

const FaceContent = () => (
  <Row>
    <Section>
      <SectionText>
        <SectionHeading>Facial verification</SectionHeading>
        <SectionParagraph>
          Can the system verify that this person is who they say they are?
        </SectionParagraph>
      </SectionText>
      <FacesContainer>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <FaceIconContainer
            style={{ height: VERIFICATION_FACE_HEIGHT, width: VERIFICATION_FACE_WIDTH }}
            src={getImgUrl(false, 'demo')}
          />
          <NameSpan>[name]</NameSpan>
        </div>
        <Arrows />
        <FaceIconContainer
          largeScreenMargin
          style={{
            height: VERIFICATION_FACE_HEIGHT,
            width: VERIFICATION_FACE_WIDTH
          }}
          src={getImgUrl(FaceStore.matchThreshold !== 9, 'matchedDemo')}
        />
      </FacesContainer>
    </Section>
    <Section hasBorderLeft>
      <SectionText>
        <SectionHeading>Facial identification</SectionHeading>
        <SectionParagraph>Can the system predict who this person may be?</SectionParagraph>
      </SectionText>
      <FacesContainer>
        <SingleFaceContainer style={{ alignSelf: 'center' }}>
          <FaceIconContainer mobileLarge src={getImgUrl(false, 'demo')} isMatched={false} />
        </SingleFaceContainer>

        <Arrows />
        <FaceGrid>
          <FaceGridMobile>
            {FACE_MATCH_THRESHOLDS.slice(0, 30).map((threshold: number, faceIdx: number) => {
              const imgNum = faceIdx + 2;
              return (
                <FaceItem>
                  <Face
                    key={`${threshold}-${faceIdx}`}
                    imgUrl={`I-${imgNum}`}
                    matchThreshold={threshold}
                  />
                </FaceItem>
              );
            })}
          </FaceGridMobile>
          <FaceGridDesktop>
            {FACE_MATCH_THRESHOLDS.map((threshold: number, faceIdx: number) => {
              const imgNum = faceIdx + 2;
              return (
                <FaceItem>
                  <Face
                    key={`${threshold}-${faceIdx}`}
                    imgUrl={`I-${imgNum}`}
                    matchThreshold={threshold}
                  />
                </FaceItem>
              );
            })}
          </FaceGridDesktop>
        </FaceGrid>
      </FacesContainer>
    </Section>
  </Row>
);

export default observer(FaceContent);
